import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useSearch } from '../../hooks/useSearch';
import { SearchCategory, TypeOfSearch } from '../../types/enums';
import { CurrencyCodeType, getCurrencyName } from '../../utils/getCurrencyName';
import { getOptions } from '../../utils/getOptions';
import { Button } from '../Button';
import { AltSelect } from '../forms/AltSelect';
import { Radio } from '../forms/Radio';
import { Search } from '../icon/icons/Search';
import { Spacer } from '../Spacer';
import { LocationSearch } from './LocationSearch';
import { usePriceOptions } from '../../hooks/usePriceOptions';

export interface MultiSearchProps {
  multiSearch: string;
  name: string;
}
interface HeroSearchProps {
  defaultMultisearch?: MultiSearchProps;
  defaultCategory?: SearchCategory;
  id?: string;
}

interface StyledInlineFiltersProps {
  visible: boolean;
}

const StyledHeroSearch = styled.div`
  position: relative;
`;

const StyledRadioGroup = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.md};
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;

    @media (min-width: ${media.lg}) {
      position: absolute;
      top: -42px;
      right: 0;
    }
  `}
`;

const StyledInlineFilters = styled.div<StyledInlineFiltersProps>`
  ${({ theme: { space, media }, visible }) => css`
    display: ${visible ? 'flex' : 'none'};
    flex-grow: 1;
    flex-direction: column;
    flex-wrap: wrap;
    gap: ${space.sm};

    @media (min-width: ${media.md}) {
      flex-direction: row;
      flex-wrap: nowrap;
    }
  `}
`;

export const HeroSearch: FC<HeroSearchProps> = ({ defaultMultisearch, defaultCategory, id = 'default' }) => {
  const { searchMeta, handleOnSubmit, formMethods } = useSearch({
    overrideValues: { type: TypeOfSearch.Residential },
    activeView: 'list',
    disableAutoSearch: true,
  });

  const { current } = useCurrencies();
  const { register, watch, setValue } = formMethods;
  const category = watch('category');
  const multiSearch = watch('multiSearch');
  const lat = watch('lat');
  const currencySaleType = category.toLowerCase();
  const currencyName = getCurrencyName(current?.code as CurrencyCodeType);

  // Create unique IDs for form elements using the component's id prop
  const buyRadioId = `buy-${id}`;
  const rentRadioId = `rent-${id}`;
  const priceLowestId = `priceLowest-${id}`;
  const priceHighestId = `priceHighest-${id}`;
  const bedroomsId = `bedrooms-${id}`;

  const initialMinPriceOptions = useMemo(
    () => searchMeta?.[`${currencySaleType}LowestPrice${currencyName}`] || [],
    [searchMeta, currencySaleType, currencyName]
  );

  const initialMaxPriceOptions = useMemo(
    () => searchMeta?.[`${currencySaleType}HighestPrice${currencyName}`] || [],
    [searchMeta, currencySaleType, currencyName]
  );

  const { minPriceOptions, maxPriceOptions } = usePriceOptions({
    initialMinPriceOptions,
    initialMaxPriceOptions,
    formMethods,
  });

  useEffect(() => {
    if (defaultMultisearch?.multiSearch) {
      setValue('multiSearch', defaultMultisearch.multiSearch);
    }
  }, [defaultMultisearch, setValue]);

  useEffect(() => {
    if (defaultCategory) {
      setValue('category', defaultCategory);
    }
  }, [defaultCategory, setValue]);

  // use effect that checks search meta and logs out the data
  useEffect(() => {
    console.log('searchMeta', searchMeta);
  }, [searchMeta]);

  return (
    <StyledHeroSearch>
      <FormProvider {...formMethods}>
        <Spacer spacing="half">
          <StyledRadioGroup>
            <Radio label="Buy" id={buyRadioId} {...register('category')} value={SearchCategory.Resale} />
            <Radio label="Rent" id={rentRadioId} {...register('category')} value={SearchCategory.Rental} />
          </StyledRadioGroup>
          <LocationSearch
            category={category}
            noIcon
            defaultMultiValue={defaultMultisearch?.name}
            placeholder="Search by location, postcode or station"
            id={`location-${id}`}
          />

          <StyledInlineFilters visible={(multiSearch || lat) && !defaultMultisearch}>
            <AltSelect
              id={priceLowestId}
              label="Min price"
              {...register('priceLowest', {
                valueAsNumber: true,
              })}
              options={getOptions(minPriceOptions)}
              constrained
            />
            <AltSelect
              id={priceHighestId}
              label="Max price"
              {...register('priceHighest', {
                valueAsNumber: true,
              })}
              options={getOptions(maxPriceOptions)}
              constrained
            />

            <AltSelect
              id={bedroomsId}
              label="Bedrooms"
              {...register('bedrooms', {
                valueAsNumber: false,
              })}
              options={[{ value: '', name: 'All' }, ...getOptions(searchMeta?.bedroomsMinimum)]}
              constrained
            />
          </StyledInlineFilters>

          <Button onClick={handleOnSubmit} type="button" iconRight={<Search />} fullWidth disableClickTracking>
            Search
          </Button>
        </Spacer>
      </FormProvider>
    </StyledHeroSearch>
  );
};
